import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Sun, Moon, ChevronDown } from 'lucide-react';
import Footer from '../components/Footer';

const FAQS = [
  {
    q: 'Who can submit a film?',
    a: 'Any filmmaker who owns the rights to their short film can submit. There\'s no entry fee, and we accept submissions year-round, with active review seasons running periodically.',
  },
  {
    q: 'How do I submit my film?',
    a: 'Sign in with Google or your email, then use the Submit Film form. You\'ll need a shareable link to your film hosted on Google Drive, YouTube, or Vimeo — we don\'t accept direct file uploads. Make sure the link\'s sharing permissions allow anyone with the link to view it.',
  },
  {
    q: 'What happens after I submit?',
    a: 'Your film moves through five stages: Submitted → Film Opened → Under Review → Shortlisted → Approved (or Not Selected). You can track exactly where your film is at any time from your dashboard.',
  },
  {
    q: 'Will I get feedback either way?',
    a: 'Yes — every submission gets honest, anonymous written feedback from our editorial team, regardless of the outcome. We believe in investing in creators, not just content.',
  },
  {
    q: 'My film link is broken or was submitted with a mistake — what now?',
    a: 'If our team spots a small, fixable issue (like a link that\'s gone private), we\'ll flag it on your dashboard with the reason and let you fix just that instead of rejecting the whole submission. You\'ll get an email when this happens.',
  },
  {
    q: 'Can I resubmit if my film isn\'t selected?',
    a: 'In some cases, yes — our team may invite you to resubmit an improved cut based on the feedback given. Watch for a resubmission link on your dashboard if this applies to you.',
  },
  {
    q: 'My film was approved — what happens next?',
    a: 'First, we\'ll send you a licensing agreement to sign (either electronically, or as a scanned copy you sign and send back). Once that\'s signed, a "Start Delivery" option unlocks on your dashboard — that\'s where you hand over your final files for release.',
  },
  {
    q: 'What do I need to provide for final delivery?',
    a: 'A Google Drive folder (or physical media, if agreed with our team) containing your final movie file, poster art, subtitle file, trailer file, banner images, and a few screengrabs/stills — plus cast & crew details and technical info like runtime and resolution, filled in on the Delivery form. Every field on that form has a small help icon (?) if you\'re unsure what it means.',
  },
  {
    q: 'My delivery failed QC — what do I do?',
    a: 'You\'ll get an email explaining exactly what needs fixing, and the same note appears on your dashboard. Go back to the Delivery page (or the "Deliver" tab in the navbar), fix what\'s flagged, and resubmit — there\'s no limit on how many times you can fix and resubmit.',
  },
  {
    q: 'What does "Published" mean?',
    a: 'It means your film has passed final QC and is live on the Shortflix platform. You\'ll see a "Published on Shortflix" badge on your dashboard once this happens.',
  },
  {
    q: 'Is there a submission limit?',
    a: 'Each review season has a submission cap to keep review quality high. If a season is full, you can still submit once the next season opens — submissions are otherwise accepted year-round.',
  },
  {
    q: 'Who sees my personal information?',
    a: 'Only the Shortflix admin team. Reviewers who watch and score your film never see your name, email, or any other contact detail — the review process is fully anonymous.',
  },
  {
    q: 'What if my question isn\'t answered here?',
    a: 'Email us directly and we\'ll get back to you.',
  },
];

export default function FAQ() {
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { user } = useAuth();
  const [openIndex, setOpenIndex] = useState(null);

  const handleCTA = () => { if (user) { navigate('/submit'); } else { navigate('/'); } };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <nav style={{ borderBottom: '1.5px solid var(--border)', padding: '0 32px', height: 58, display: 'flex', alignItems: 'center', gap: 14, background: 'var(--nav-bg)', backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)', position: 'sticky', top: 0, zIndex: 100 }}>
        <img src="/logo.png" alt="Shortflix" style={{ height: 28, cursor: 'pointer' }} onClick={() => navigate(user ? '/dashboard' : '/')} onError={e => { e.target.style.display = 'none'; }} />
        <span style={{ color: 'var(--border-strong)', fontSize: 18 }}>|</span>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>FAQ</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={toggle} style={{ width: 32, height: 32, borderRadius: 'var(--r-sm)', background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            {theme === 'dark' ? <Sun size={13} color="var(--text-secondary)" /> : <Moon size={13} color="var(--text-secondary)" />}
          </button>
          <button onClick={handleCTA} className="btn-primary" style={{ padding: '7px 16px', fontSize: 11 }}>{user ? 'My Dashboard' : 'Sign In to Submit →'}</button>
        </div>
      </nav>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '52px 32px', flex: 1, width: '100%' }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--pink)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>Help</div>
        <h1 style={{ fontWeight: 900, fontSize: 38, color: 'var(--text)', marginBottom: 6, letterSpacing: '-0.02em' }}>Frequently Asked Questions</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 36, fontWeight: 400 }}>Everything you need to know about submitting to Shortflix.</p>

        {FAQS.map((item, i) => {
          const open = openIndex === i;
          return (
            <div key={i} className="card" style={{ padding: 0, marginBottom: 10, overflow: 'hidden' }}>
              <button onClick={() => setOpenIndex(open ? null : i)} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '16px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left', fontFamily: 'var(--font)' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{item.q}</span>
                <ChevronDown size={16} color="var(--text-muted)" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0, marginLeft: 12 }} />
              </button>
              {open && (
                <div style={{ padding: '0 18px 16px' }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.7, fontWeight: 400 }}>{item.a}</p>
                </div>
              )}
            </div>
          );
        })}

        <hr className="divider" style={{ margin: '32px 0 20px' }} />
        <p style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 500 }}>
          Still have questions? Email us at <a href="mailto:info@shortflixindia.com" style={{ color: 'var(--pink)', fontWeight: 700 }}>info@shortflixindia.com</a>.
        </p>
      </div>

      <Footer />
    </div>
  );
}
